import { ChangeDetectorRef, Component, ElementRef, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { map, Subscription, take } from 'rxjs';
import { AppService } from '../../app.service';
import { FieldDefinition } from '../../models/fieldDefinition';
import { MetadataField } from '../../models/metadataField';
import { MetadataInfo } from '../../models/metadataInfo';
import { MetaDataRuleDefinition } from '../../models/metaDataRuleDefinition';
import { TransformationRule } from '../../models/transformationRule';
import { TransformationRuleDefinition } from '../../models/transformationRuleDefinition';
import { FileDefinitionService } from '../../services/FileDefinitionService';
import { uniqueMetadataName } from '../../theme/utils/app-validators';

@Component({ standalone:false,
  selector: 'app-edit-metadata-dialog',
  templateUrl: './edit-metadata-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-metadata-dialog.component.scss']
})
export class EditMetadataDialogComponent {
  public saveButtonText = "Save";
  public cancelButtonText = "Cancel";
  public selectedMetadataRule?: MetaDataRuleDefinition;

  private metadataInfo!:MetadataInfo;
  public metadataForm: FormGroup;
  public metadataRules: MetaDataRuleDefinition[]=[];
  public availableFields: FieldDefinition[]=[];
  public namesTaken:string[]=[];

  private metadataRuleChangedSubscription?:Subscription;
  private transformationRuleChangedSubscriptionArray:Subscription[]=[];

  private initialFormValues: any; 

  constructor(private fdService:FileDefinitionService, private dialogRef: MatDialogRef<EditMetadataDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any, private cd:ChangeDetectorRef) {
    this.metadataInfo=data.metadataInfo;
    this.metadataRules=data.metadataRuleDefinitions;
    this.availableFields=data.availableFields;
    this.namesTaken = data.namesTaken;
    this.metadataForm = new FormGroup({
        metadataName: new FormControl(null, Validators.compose([uniqueMetadataName(this.namesTaken), Validators.required, Validators.minLength(5), Validators.maxLength(100)])),
        metadataRule: new FormControl(null, Validators.compose([Validators.required])),
        fieldSelection: new FormControl(null),
        staticValue: new FormControl(null),
        transformationRules: new FormArray([])
    });
  }

  ngOnInit() {
    if(this.metadataInfo) this.populateForm();
    this.initialFormValues=this.metadataForm.value;
    this.metadataRuleChangedSubscription = this.metadataForm.get('metadataRule')!.valueChanges.subscribe(event=> this.onSelectMetadataRule(event));
  }

  ngOnDestroy(){
    if(this.metadataRuleChangedSubscription) this.metadataRuleChangedSubscription.unsubscribe();
    this.transformationRuleChangedSubscriptionArray.forEach(sub=>sub.unsubscribe());
    this.transformationRuleChangedSubscriptionArray=[];
  }

  get canSaveMetadata(){
    return this.metadataForm.valid && (this.metadataForm.touched || this.metadataForm.dirty);
  }

  get needsInput(){
    return this.selectedMetadataRule?.id!=9;
  }

  get canSelectFields(){
    return this.selectedMetadataRule && (this.selectedMetadataRule.fieldSelection=="s" || this.selectedMetadataRule.fieldSelection=="m");
  }

  get canSelectMultipleFields(){
    return this.selectedMetadataRule && this.selectedMetadataRule?.fieldSelection=="m";
  }

  get filteredAvailableFields(){
    if(this.selectedMetadataRule){
      return this.availableFields.filter(f=> this.selectedMetadataRule!.fieldDataType=="*" || f.dataType==this.selectedMetadataRule!.fieldDataType);
    }
    else return undefined;
  }

  get transformationRulesControls(): FormGroup[] {
    return (this.metadataForm.get('transformationRules') as FormArray).controls as FormGroup[];
  }

  public dataPatternArray(dataType:string){
    if(dataType=='DATE'){
      return AppService.dataPatternArrayDate;
    }
    else{
      return [];
    }
  }

  private populateForm(){
    this.metadataForm.get("metadataName")!.setValue(this.metadataInfo.name);
    let rule=this.metadataRules.find(mdr=>mdr.id==this.metadataInfo.ruleId)
    this.metadataForm.get("metadataRule")!.setValue(rule);
    this.selectedMetadataRule=this.metadataForm.get("metadataRule")!.value;
    if(rule?.fieldSelection=="s"){
      this.metadataForm.get("fieldSelection")!.setValue(this.availableFields.find(af=> this.metadataInfo.fields.find(mdf=>af.matchesMetadataField(mdf))));
    }   
    else if(rule?.fieldSelection=="m"){
      this.metadataForm.get("fieldSelection")!.setValue(this.availableFields.filter(af=> this.metadataInfo.fields.find(mdf=>af.matchesMetadataField(mdf))));
    }
    else if(rule?.fieldSelection=="n"){
      this.metadataForm.get("staticValue")!.setValue((this.metadataInfo.fields?.length>0)?this.metadataInfo.fields[0].name:'');
    }
    if(this.metadataInfo.transformationRules){
      this.metadataInfo.transformationRules.forEach(tr=>{
        if(!tr.transformationRuleDefinition){
          this.fdService.getTransformationRuleDefinitionById(tr.transformationRuleDefinitionId).pipe(take(1)).subscribe(trd=>{
            tr.transformationRuleDefinition=trd!;
            this.populateRuleFormGroup(tr);
          });
        }
        else{
          this.populateRuleFormGroup(tr);
        }
      });
    }
  }

  private populateRuleFormGroup(tr:TransformationRule){
    let ruleFormGroup = this.addTransformationRule();
    ruleFormGroup.controls.id.setValue(tr.id);
    ruleFormGroup.controls.sequenceNo.setValue(tr.sequenceNo);
    ruleFormGroup.controls.transformationRuleDefinition.setValue(tr.transformationRuleDefinition);
    ruleFormGroup.controls.transformationParameter1.setValue(tr.transformationParameter1);
    ruleFormGroup.controls.transformationParameter2.setValue(tr.transformationParameter2);
    ruleFormGroup.controls.inputPassthrough.setValue(tr.inputPassthrough);
    ruleFormGroup.controls.breakOnConditionMet.setValue(tr.breakOnConditionMet);
  }

  public recordsIncludedSelector(value: string) {
    switch(value){
      case 'a':
        return "All records in the dataset";
      case 'f':
        return "First Record";
      case 'l':
        return "Last Record";
      default:
        return null;
    }
  }

  public onSelectMetadataRule(event:any){
    this.selectedMetadataRule=event;
    this.metadataForm.get("fieldSelection")!.setValue(null);
    this.metadataForm.get("staticValue")!.setValue(null);
    if(this.needsInput){
      if(this.canSelectFields){
        this.metadataForm.get("fieldSelection")!.setValidators(Validators.required);
        this.metadataForm.get("staticValue")!.clearValidators();
      }
      else{
        this.metadataForm.get("fieldSelection")!.clearValidators();
        this.metadataForm.get("staticValue")!.setValidators(Validators.required);
      }
    }
    else{
      this.metadataForm.get("fieldSelection")!.clearValidators();
      this.metadataForm.get("staticValue")!.clearValidators();
    }
    this.metadataForm.get("fieldSelection")!.updateValueAndValidity();
    this.metadataForm.get("staticValue")!.updateValueAndValidity();
    this.cd.detectChanges();
  }

  //#region transformation rules
  public transformationRuleDefinitions(ruleIndex:number){
    return this.fdService.getTransformationRuleDefinitions(false).pipe(map((r)=>{
      return this.filterApplicableTransformationRuleDefinitions(ruleIndex, r);
    }));
  }

  private filterApplicableTransformationRuleDefinitions(ruleIndex:number, ruleDefinitions:TransformationRuleDefinition[]){
    //filter for file type first
    if(!this.selectedMetadataRule) return [];
    let filteredRuleDefinitions:TransformationRuleDefinition[] = ruleDefinitions.filter(d=>(d.canUseForFileType("csv"))); 
    const rules = this.metadataForm.get('transformationRules') as FormArray;
    if(rules.value && rules.value[ruleIndex]){
      if(ruleIndex==0){
        filteredRuleDefinitions = filteredRuleDefinitions.filter(d=>(d.inputDataCount==1 && (d.inputDataType==this.selectedMetadataRule!.fieldDataType || d.inputDataType=='*' || this.selectedMetadataRule!.fieldDataType=='*'))); 
      }
      return filteredRuleDefinitions;
    }
    else return [];
  }

  public createTransformationRule(index:number): FormGroup {
    const fbGroup = new FormGroup({
      id: new FormControl(0),
      sequenceNo: new FormControl(index, Validators.required),
      transformationRuleDefinition: new FormControl<TransformationRuleDefinition | null>(null, Validators.compose([Validators.required])),
      transformationParameter1: new FormControl(''),
      transformationParameter2: new FormControl(''),
      inputPassthrough: new FormControl(false),
      breakOnConditionMet: new FormControl(false)
    });

    const sub = fbGroup.controls.transformationRuleDefinition.valueChanges.subscribe(() => {
      fbGroup.controls.transformationParameter1.setValue('');
      fbGroup.controls.transformationParameter2.setValue('');
      fbGroup.controls.inputPassthrough.setValue(false);
      fbGroup.controls.breakOnConditionMet.setValue(false);
      this.cd.detectChanges();
    });
    this.transformationRuleChangedSubscriptionArray.push(sub);
    return fbGroup;
  }

  public addTransformationRule() {
    const rules = this.metadataForm.get('transformationRules') as FormArray;
    let rule=this.createTransformationRule(rules.length);
    rules.push(rule);
    return rule;
  }

  public deleteTransformationRule(ruleIndex:number) {
    const rules = this.metadataForm.get('transformationRules') as FormArray;
    rules.removeAt(ruleIndex);
    rules.markAsTouched();
  }

  public deleteAllTransformationRules() {
    const rules = this.metadataForm.get('transformationRules') as FormArray;
    rules.clear();
    rules.markAsTouched();
  }
  
  private createTempTransformationRules(fgArray:any){
    var transformationRuleArray=new Array();
    if(fgArray && fgArray.length>0){
      fgArray.forEach((fg:any)=>{
        let tr =new TransformationRule();
        tr.id=fg.id;
        tr.sequenceNo=fg.sequenceNo,
        tr.transformationRuleDefinition=fg.transformationRuleDefinition;
        tr.transformationParameter1=fg.transformationParameter1;
        tr.transformationParameter2=fg.transformationParameter2;
        tr.inputPassthrough=fg.inputPassthrough;
        tr.breakOnConditionMet=fg.breakOnConditionMet;
        transformationRuleArray.push(tr);
      });
    }
    return transformationRuleArray;
  }
  //#endregion

  onCancelClick(): void {
    this.metadataForm.reset(this.initialFormValues);
    this.dialogRef.close(undefined);
  }

  onSaveClick(): void{
    //create a metadataInfo object and return it
    let mi = new MetadataInfo();
    mi.name = this.metadataForm.get("metadataName")!.value.trim();
    let rule:MetaDataRuleDefinition = this.metadataForm.get("metadataRule")!.value;
    mi.ruleId = rule.id;
    if(rule.fieldSelection=="s"){
      let fd:FieldDefinition = this.metadataForm.get("fieldSelection")!.value;
      mi.fields.push(fd.metaDataField);
    }   
    else if(rule.fieldSelection=="m"){
      let fieldSelection:FieldDefinition[] = this.metadataForm.get("fieldSelection")!.value;
      mi.fields=fieldSelection.map(f=>f.metaDataField);
    }
    else if(rule.fieldSelection=="n"){
      let mdf = new MetadataField();
      mdf.index = -1;
      mdf.name = this.metadataForm.get("staticValue")!.value;
      mi.fields.push(mdf);
    }
    mi.transformationRules=this.createTempTransformationRules(this.metadataForm.get('transformationRules')!.value);
    this.dialogRef.close(mi);
  }

}
